import { useGetMe } from 'api-hooks/auth/query';
import { GuestModel } from 'api-hooks/guest';
import {
  ActivitySaleModel,
  ActivitySaleMutationInput,
} from 'api-hooks/sale/activity';
import { AuthorizationRules } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import { generateId } from 'common/utils/string';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { CustomLabel } from 'components/elements/custom-form-control';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import useCreateGuestAction from 'legacy-modules/master/guest/hooks/use-create-guest-action';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import ParentSaleRelatedTable from 'legacy-modules/sales/component/parent-children-sales-table';
import PurchaseRelatedTable from 'legacy-modules/sales/component/purchase-related-table';
import SalesActionComponent from 'legacy-modules/sales/component/sales-action-component';
import RescheduleHistoryTable from 'legacy-modules/sales/reschedule/reschedule-history-table';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  Container,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import ActivitySection from './activity';
import {
  ActivitySaleFormSchema,
  ActivitySaleFormType,
  ActivitySaleGuestItemFormType,
  ActivitySaleItemFormType,
  ActivitySaleMethodType,
} from './form-type';
import useGetDefaultValues, {
  addNewGuestDetailItem,
} from './hooks/use-get-default-values';
import SalesDetailCard from './sales-detail-card';
import ActivitySaleSelectInput from './select-input';
import SummaryActivitySale from './summary';
import { totalActivitySaleCalculation } from './total-calculation';
import { FormContent, FormDivider, OtherContainer } from '../style';

interface Props {
  activitySale?: ActivitySaleModel;
  onSubmit: (
    input: ActivitySaleMutationInput,
    form: ActivitySaleMethodType,
  ) => Promise<ActivitySaleModel | undefined>;
  renderError?: React.ReactNode;
  defaultValues?: ActivitySaleFormType;
}

export default function ActivitySaleForm(props: Props) {
  const { t } = useTranslation();
  const { activitySale, renderError } = props;
  const { can } = useAuthorization();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    activitySale,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(ActivitySaleFormSchema());

  const methods = useForm<ActivitySaleFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: ActivitySaleFormType) => {
      const {
        activitySales,
        tempTransactionAt,
        customerBillingOptions,
        address,
        phone,
        hasDueReceivable,
        customerStatus,
        isExceedingQuota,
        remainingTransactionQuota,
        // remove unused data
        ...rest
      } = values;
      try {
        const total = totalActivitySaleCalculation({
          activitySales: values.activitySales,
          discount: values.discount,
          otherCost: values.otherCost,
        });

        const as = activitySales.map((as) => ({
          ...as,
          activitySaleGuests: as.activitySaleGuests.map((asg, idx) => {
            const preferences = (asg.preferences || []).map(
              (preference) => preference.id,
            );

            const defaultAsg = {
              ...asg,
              preferences,
            };

            if (as.isGroupedPrice && idx > 0) {
              defaultAsg.activitySaleGuestDetails =
                as.activitySaleGuests[0].activitySaleGuestDetails;
            }

            return defaultAsg;
          }),
        }));

        const needApproval =
          values.transactionAt!.getTime() !== tempTransactionAt!.getTime();

        const result = {
          ...rest,
          total,
          activitySales: as,
          needApproval,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const isContribute =
    activitySale?.admin.id === employee?.id ||
    activitySale?.salesman?.id === employee?.id ||
    activitySale?.operator?.id === employee?.id;

  const core = can(AuthorizationRules.SalesCore);

  const onRenderComponent = activitySale && (isContribute || core) && (
    <>
      <SalesActionComponent
        data={activitySale}
        defaultValues={defaultValues}
        type="activity-sales"
      />
    </>
  );

  const customViewName =
    activitySale &&
    `${t('common:others_sale')} - ${activitySale.invoiceNumber}`;

  //autofill
  const { getValues, setValue } = methods;
  const onSuccessCreatGuest = React.useCallback(
    (guest: GuestModel) => {
      const guestName = guestNameTransformer(guest);
      const activitySales: ActivitySaleItemFormType[] = getValues(
        'activitySales',
      ).map((ps) => {
        const activitySaleGuestDetails = [addNewGuestDetailItem()];
        const activitySaleGuests: ActivitySaleGuestItemFormType[] = [
          ...ps.activitySaleGuests,
          {
            customId: generateId(),
            guestId: guest.id,
            guestName,
            activitySaleGuestDetails: ps.isGroupedPrice
              ? ps.activitySaleGuests?.[0]?.activitySaleGuestDetails ??
                activitySaleGuestDetails
              : activitySaleGuestDetails,
            preferences: [],
            id: undefined,
          },
        ];
        return {
          ...ps,
          activitySaleGuests,
        };
      });
      setValue('activitySales', activitySales);
    },
    [getValues, setValue],
  );
  const { createGuestComponent } = useCreateGuestAction({
    onSuccess: onSuccessCreatGuest,
  });

  const onRelatedInvoiceAfterChange = React.useCallback(
    (value: ActivitySaleModel) => {
      setValue('parentRefId', value.refId);
      setValue('operatorId', value.operator?.id || '');
      setValue('salesmanId', value.salesman?.id || '');
      setValue('customerId', value.customer?.id || '');
      setValue('customerBillingId', value.customerBillingAddress?.id || '');
    },
    [setValue],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!activitySale}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormContent>
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={activitySale}
              noSubmit
              noCancel
              noDelete
              noEdit
              title={
                activitySale ? (
                  <TitleStatus
                    type="sale-default"
                    title={customViewName ?? t('common:others_sale')}
                    isCompleted={activitySale.isCompleted}
                    status={activitySale.status}
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:others_sale') })
                )
              }
              onRenderComponent={
                editable ? createGuestComponent : onRenderComponent
              }
            />
          )}
        </FormState>
        {renderError}
        <div>
          {activitySale && !!(activitySale?.releaseSales || []).length && (
            <SalePurchaseRefundTable data={activitySale.releaseSales} />
          )}
          {activitySale &&
            !!activitySale.purchases.length &&
            (isContribute || core) && (
              <PurchaseRelatedTable
                data={activitySale.purchases}
                navigation="ActivityPurchasesView"
              />
            )}
          {activitySale && (
            <RescheduleHistoryTable
              saleId={activitySale.id}
              type="activity"
              hide
            />
          )}
          {activitySale &&
            (!!activitySale.parentSale ||
              !!(activitySale.childrenSales || []).length) && (
              <ParentSaleRelatedTable
                data={activitySale}
                navigation="ActivitySalesView"
              />
            )}
          <RawSectionContainer>
            <HalfContainer>
              <ActivitySaleSelectInput
                label={t('common:related_invoice')}
                placeholder={t('common:choose_invoice')}
                name="parentRefId"
                onAfterDetailChange={onRelatedInvoiceAfterChange}
                isSales
                type="invoice"
              />
            </HalfContainer>
          </RawSectionContainer>
          <RawSectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('ticketSales:group_code')}
                placeholder={t('ticketSales:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </RawSectionContainer>
          <SeparatorVertical $margin={16} />
          <SalesDetailCard activitySale={activitySale} />
          <SeparatorVertical $margin={46}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <ActivitySection />
          <SeparatorVertical $margin={24}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <Container $style={{ height: 'auto' }}>
            <OtherContainer>
              <CustomLabel label={t('common:discount')} />
              <Input
                type="number"
                name="discount"
                placeholder={t('common:discount')}
                textAlign="right"
                clearable={false}
                noMargin
              />
            </OtherContainer>
            <SeparatorVertical $margin={16} />
            <OtherContainer>
              <CustomLabel label={t('common:other')} />
              <Input
                type="number"
                name="otherCost"
                placeholder={t('common:other')}
                textAlign="right"
                clearable={false}
                noMargin
              />
            </OtherContainer>
          </Container>
          <SeparatorVertical $margin={32} />
          <FormDivider />
        </div>
      </FormContent>
      <SummaryActivitySale />
    </Form>
  );
}
